/**
 * Generic Form Data to JSON Handler
 *
 * Automatically collects form data using [data-json-path] attributes,
 * builds a nested JSON structure, and submits to API.
 *
 * Features:
 *  - Google Places Autocomplete for address lookup
 *  - Geolocation-based autocomplete bounds
 *  - Phone number key validation
 *  - Input validation helpers
 *  - Loading state management
 */

"use strict";

// ---------------------------------------------------------------------------
// Constants & Configuration
// ---------------------------------------------------------------------------

const DEFAULTS = {
  STORE_ID: "main_us",
  REDIRECT_URL: "https://account.restaurantware.com",
  SUBMIT_BUTTON_SELECTOR: ".company_custom_button",
  FORM_ID: "registration-form",
  STREET_INPUT_ID: "street_1",
  MAX_UPLOAD_BYTES: 20 * 1024 * 1024,
};

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

let _autocompleteStreet1 = null;

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

/**
 * Recursively set a value inside a nested object using dot-notation.
 *
 * @param {Object} obj   - Target object.
 * @param {string} path  - Dot-notation path, e.g. "customer.address.city".
 * @param {*}      value - Value to assign.
 */
function setNestedValue(obj, path, value) {
  const keys = path.split(".");
  let cursor = obj;

  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (!cursor[key] || typeof cursor[key] !== "object") {
      cursor[key] = {};
    }
    cursor = cursor[key];
  }

  cursor[keys[keys.length - 1]] = value;
}

/**
 * Allow only phone-safe characters on keypress.
 * Permitted: digits, spaces, hyphens, parentheses, plus sign.
 *
 * @param {KeyboardEvent} evt
 * @returns {boolean}
 */
function onlyNumberKey(evt) {
  const code = evt.which ?? evt.keyCode;
  if (code === 8 /* Backspace */ || code === 46 /* Delete */) return true;
  return /[0-9()+\s-]/.test(String.fromCharCode(code));
}

/**
 * Mark an input invalid when its trimmed value is empty.
 *
 * @param {HTMLInputElement} input
 */
function validateInput(input) {
  input.setCustomValidity(
    input.value.trim() === "" ? "Please enter a valid input" : "",
  );
}

// ---------------------------------------------------------------------------
// Form Data Collection
// ---------------------------------------------------------------------------

/**
 * Walk all [data-json-path] elements in the form and build a nested JSON
 * object. The initial skeleton guarantees expected top-level keys are always
 * present even when no matching inputs exist.
 *
 * @param {HTMLFormElement} form
 * @returns {Object} Nested JSON ready for API submission.
 */
function collectFormDataAsJSON(form) {
  const payload = {
    customer: { address: {} },
    additionalInformation: { accountsPayable: {} },
  };

  form.querySelectorAll("[data-json-path]").forEach((input) => {
    // File inputs are handled separately (async) by attachFilesToPayload,
    // and disabled fields (hidden/inactive conditional sections) are excluded.
    if (input.disabled || input.type === "file") return;

    const path = input.getAttribute("data-json-path");

    // Resolve value by input type; null signals "skip this field"
    let value;
    if (input.type === "checkbox") value = input.checked;
    else if (input.type === "radio") value = input.checked ? input.value : null;
    else value = input.value;

    if (value !== null && value !== "") {
      setNestedValue(payload, path, value);
    }
  });

  return payload;
}

/**
 * Read a File object and resolve it to a base64-encoded payload fragment.
 *
 * @param {File} file
 * @returns {Promise<{fileName: string, contentType: string, size: number, data: string}>}
 */
function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const result = String(reader.result ?? "");
      const base64Data = result.substring(result.indexOf(",") + 1);

      resolve({
        fileName: file.name,
        contentType: file.type,
        size: file.size,
        data: base64Data,
      });
    };

    reader.onerror = () => reject(reader.error ?? new Error("File read failed"));

    reader.readAsDataURL(file);
  });
}

/**
 * Find every enabled `[data-json-path]` file input in the form, read its
 * selected file as base64, and attach the result to the payload at the
 * input's JSON path. Forms without file inputs resolve immediately.
 *
 * @param {HTMLFormElement} form
 * @param {Object} payload
 * @returns {Promise<void>}
 */
async function attachFilesToPayload(form, payload) {
  const fileInputs = form.querySelectorAll(
    'input[type="file"][data-json-path]:not(:disabled)',
  );

  for (const input of fileInputs) {
    const file = input.files?.[0];
    if (!file) continue;

    const path = input.getAttribute("data-json-path");
    const fileData = await readFileAsBase64(file);
    setNestedValue(payload, path, fileData);
  }
}

// ---------------------------------------------------------------------------
// Country select -> hidden country-code sync
// ---------------------------------------------------------------------------

/**
 * Sync the hidden ISO country-code field from the selected `#customer_country`
 * option's `data-code` attribute (set by the section's custom
 * `localization.available_countries` loop). Referenced globally as
 * `onchange="updateStates()"` by every company registration section's
 * country <select> — without it the hidden `customer_country_code` field
 * (required by Shopify's companyLocationCreate mutation) is never populated.
 */
function updateStates() {
  const countrySelect = document.getElementById("customer_country");
  const countryCodeField = document.getElementById("customer_country_code");

  if (!countrySelect || !countryCodeField) return;

  const selectedOption = countrySelect.options[countrySelect.selectedIndex];
  countryCodeField.value = selectedOption?.dataset.code ?? "";
}

document.addEventListener("DOMContentLoaded", updateStates);

// ---------------------------------------------------------------------------
// Google Places — Autocomplete
// ---------------------------------------------------------------------------

/**
 * Initialise Google Places Autocomplete on the street address input.
 * Intended to be called as the `callback` of the Maps JS API script tag
 * (i.e. `&callback=initAutocomplete`).
 */
function initAutocomplete() {
  const streetInput = document.getElementById(DEFAULTS.STREET_INPUT_ID);

  if (!streetInput) {
    console.warn(
      `[FormHandler] Element #${DEFAULTS.STREET_INPUT_ID} not found.`,
    );
    return;
  }

  streetInput.addEventListener("focus", geolocate, true);

  _autocompleteStreet1 = new google.maps.places.Autocomplete(streetInput, {
    types: ["geocode"],
  });

  _autocompleteStreet1.addListener("place_changed", fillInAddress);
}

/**
 * Bias autocomplete results toward the user's current position.
 */
function geolocate() {
  if (!navigator.geolocation || !_autocompleteStreet1) return;

  navigator.geolocation.getCurrentPosition(({ coords }) => {
    const circle = new google.maps.Circle({
      center: { lat: coords.latitude, lng: coords.longitude },
      radius: coords.accuracy,
    });
    _autocompleteStreet1.setBounds(circle.getBounds());
  });
}

/**
 * Populate address fields from the selected Place.
 * Called automatically by the `place_changed` listener.
 */
function fillInAddress() {
  if (!_autocompleteStreet1) return;

  const place = _autocompleteStreet1.getPlace();

  if (!place?.address_components) {
    console.warn(
      "[FormHandler] place_changed: no address_components returned.",
    );
    return;
  }

  // Small inline helper — used only within this function's loop
  const set = (id, val) => {
    const el = document.getElementById(id);
    if (el) el.value = val;
  };

  let streetNumber = "";
  let streetRoute = "";

  place.address_components.forEach(({ types, long_name, short_name }) => {
    if (types.includes("street_number")) {
      streetNumber = long_name;
    }

    if (types.includes("route")) {
      streetRoute = long_name;
    }

    if (types.includes("locality")) {
      set("customer_notes_City", long_name);
    }

    if (types.includes("administrative_area_level_1")) {
      set("customer_notes_Province", long_name);
      set("customer_notes_ProvinceCode", short_name);
    }

    if (types.includes("postal_code")) {
      set("customer_notes_Zip", long_name);
    }

    if (types.includes("country")) {
      const countrySelect = document.getElementById("customer_country");
      if (countrySelect) {
        const match = Array.from(countrySelect.options).find(
          (opt) => opt.text === long_name,
        );
        if (match) countrySelect.value = match.value;
      }
      set("customer_notes_country", long_name);
      set("customer_country_code", short_name);
    }
  });

  set(
    DEFAULTS.STREET_INPUT_ID,
    [streetNumber, streetRoute].filter(Boolean).join(" "),
  );
}

// ---------------------------------------------------------------------------
// Loading State Management
// ---------------------------------------------------------------------------

/**
 * Show loading spinner and disable submit button.
 *
 * @param {HTMLElement} submitBtn - The submit button
 * @param {HTMLElement} spinnerElement - The spinner element
 */
function showLoadingState(submitBtn, spinnerElement) {
  if (spinnerElement) {
    spinnerElement.style.display = "block";
  }
  if (submitBtn) {
    submitBtn.disabled = true;
  }
}

/**
 * Hide loading spinner and re-enable submit button.
 * Restores UI to original state.
 *
 * @param {HTMLElement} submitBtn - The submit button
 * @param {HTMLElement} spinnerElement - The spinner element
 */
function hideLoadingState(submitBtn, spinnerElement) {
  if (spinnerElement) {
    spinnerElement.style.display = "none";
  }
  if (submitBtn) {
    submitBtn.disabled = false;
  }
}

// ---------------------------------------------------------------------------
// API Submission
// ---------------------------------------------------------------------------

/**
 * POST the collected form payload to the registration API endpoint.
 * Handles three response cases:
 * 1. Success - redirect to redirect URL
 * 2. Account already exists - hide spinner, reset button, redirect to failure page
 * 3. Failed to create account - hide spinner, reset button, redirect to failure page
 *
 * @param {Object} payload - Nested JSON built by `collectFormDataAsJSON`.
 */
function submitFormToAPI(payload) {
  const submitBtn = document.querySelector(DEFAULTS.SUBMIT_BUTTON_SELECTOR);
  const spinnerElement = document.querySelector(".customer_login_spinner");
  const storeId = window.STORE_ID ?? DEFAULTS.STORE_ID;
  const failurePageUrl = "/pages/account-creation-failed";
  const registrationHashElement = document.getElementById("customer-registration-hash");
  const pageId = payload?.metadata?.pageId;
  const hashKey = registrationHashElement?.dataset.hashKey;

  if (!pageId || !hashKey) {
    console.error("[FormHandler] Page ID or registration hash is missing.");
    hideLoadingState(submitBtn, spinnerElement);
    window.location.href = failurePageUrl + "?statusCode=500";
    return;
  }

  // Use localhost endpoint for development
  const endpoint = `https://api.restaurantware.com/api/v1/${storeId}/customer/create/${encodeURIComponent(pageId)}?key=${encodeURIComponent(hashKey)}`;

  // Show spinner and disable button to prevent multiple submissions
  showLoadingState(submitBtn, spinnerElement);

  fetch(endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  })
    .then((response) => {
      // Store status code for later use
      const statusCode = response.status;
      
      return response.json().then((data) => {
        // Return both data and status code
        return { data, statusCode, ok: response.ok };
      });
    })
    .then(({ data, statusCode, ok }) => {
      console.debug("[FormHandler] Response received:", data, "Status:", statusCode);

      // Handle success response (HTTP 200)
      if (ok && data.code === 0) {
        // Case 1: Success (code 0) - redirect to success URL (spinner stays visible during redirect)
        console.debug("[FormHandler] Account creation successful");
        window.location.href = data.redirectUrl ?? DEFAULTS.REDIRECT_URL;
      }
      // Handle 409 Conflict - Account already assigned to company
      else if (statusCode === 409 || data.code === 98) {
        // Case 2: Account already assigned to company (HTTP 409)
        console.warn("[FormHandler] Account already assigned to company (HTTP 409)");
        window.location.href = failurePageUrl + "?statusCode=409";
      }
      // Handle any other error response
      else {
        // Case 3: Any other code - failed to create account
        console.error("[FormHandler] Account creation failed (code " + data.code + ", HTTP " + statusCode + ")");
        window.location.href = failurePageUrl + "?statusCode=500";
      }
    })
    .catch((error) => {
      console.error("[FormHandler] Submission error:", error);
      hideLoadingState(submitBtn, spinnerElement);
      window.location.href = failurePageUrl + "?statusCode=500";
    });
}

// ---------------------------------------------------------------------------
// Initialisation
// ---------------------------------------------------------------------------

/**
 * Attach the submit listener to the registration form.
 * Called once the DOM is ready.
 */
function initializeFormHandler() {
  const form = document.getElementById(DEFAULTS.FORM_ID);

  if (!form) {
    console.warn(`[FormHandler] Form #${DEFAULTS.FORM_ID} not found.`);
    return;
  }

  form.addEventListener("submit", (event) => {
    event.preventDefault();

    const submitBtn = document.querySelector(DEFAULTS.SUBMIT_BUTTON_SELECTOR);
    const spinnerElement = document.querySelector(".customer_login_spinner");
    const payload = collectFormDataAsJSON(form);

    showLoadingState(submitBtn, spinnerElement);

    attachFilesToPayload(form, payload)
      .then(() => {
        console.debug("[FormHandler] Submitting payload:", payload);
        submitFormToAPI(payload);
      })
      .catch((error) => {
        console.error("[FormHandler] Failed to attach file(s) to payload:", error);
        hideLoadingState(submitBtn, spinnerElement);
        window.location.href = "/pages/account-creation-failed?statusCode=500";
      });
  });
}

document.addEventListener("DOMContentLoaded", initializeFormHandler);
